// trendsReportUtils.ts
// Helper functions for TrendsReportBuilder (Composition API + TypeScript ready)
/**
 * Add the value to the total.
 * If the value is null, the total will not be updated.
 * This is needed because in JS, null + null = 0.
 * In the context of our data, null represents no data while 0 represents no production.
 * @param {number | null} value - The value to add.
 * @param {number | null} total - The current total.
 * @returns {number | null} The updated total or null if no values were added.
 */
export function addValueToTotal(value, total) {
    if (value !== null) {
        if (total === null) {
            total = 0;
        }
        total += value;
    }
    return total;
}
/**
 * Add the value to the total for a specific unit.
 * If the value is null, the total will not be updated.
 * @param {number | null} value - The value to add.
 * @param {string} unit - The unit of the value.
 * @param {Record<string, number>} unitTotal - The current total by unit.
 * @returns {Record<string, number>} The updated total by unit.
 */
export function addValueToUnitTotal(value, unit, unitTotal) {
    if (value !== null) {
        if (!unitTotal[unit]) {
            unitTotal[unit] = 0;
        }
        unitTotal[unit] += value;
    }
    return unitTotal;
}
/**
 * Get the period key in ISO format. Use a single format to avoid inconsistencies.
 * This will be used as the key for the objects.
 * @param {string | Date} period - The period to format.
 * @returns {string} The period in ISO format.
 */
export function getPeriodKey(period) {
    return new Date(period).toISOString();
}
/**
 * Calculate the performance
 * @param {number} production - The actual production.
 * @param {number} expected_production - The expected production.
 * @returns {number} The performance as a ratio (0 to 1).
 */
export function calculatePerformance(production, expected_production) {
    return production / expected_production;
}
/**
 * Calculate the availability
 * @param {number} operating_time - The operating time.
 * @param {number} available_time - The available time.
 * @returns {number} The availability as a ratio (0 to 1).
 */
export function calculateAvailability(operating_time, available_time) {
    return operating_time / available_time;
}
/**
 * Calculate the quality
 * @param {number} net_production - The net production (production - reject_production).
 * @param {number} total_production - The total production.
 * @returns {number} The quality as a ratio (0 to 1).
 */
export function calculateQuality(net_production, total_production) {
    return net_production / total_production;
}
// --- Helpers ---
/**
 * Initialize product/period structure for productsData
 * @param {string[]} periods - The periods to initialize.
 * @returns {Record<string, TrendPeriodData | null>} The initialized product periods.
 */
export function initProductPeriod(periods) {
    return periods.reduce((acc, period) => {
        acc[period] = null;
        return acc;
    }, {});
}
/**
 * Update productsData for a record
 * @param {ProductsData} productsData - The products data to update.
 * @param {TrendRecord} record - The trend record to add.
 * @param {string[]} periods - The list of periods to initialize.
 * @param {Set<string>} initializedProducts - A set to track initialized products.
 */
export function updateProductData(productsData, record, periods, initializedProducts) {
    const { asset_id, product_id, period } = record;
    if (!productsData[asset_id]) {
        productsData[asset_id] = {};
    }
    const assetProductKey = `${asset_id}_${product_id}`;
    if (!initializedProducts.has(assetProductKey)) {
        productsData[asset_id][product_id] = {
            ...initProductPeriod(periods),
            product_name: record.product_name,
            unit_measure: record.unit_measure
        };
        initializedProducts.add(assetProductKey);
    }
    const periodKey = getPeriodKey(period);
    productsData[asset_id][product_id][periodKey] = {
        oee: record.oee,
        availability: record.availability,
        performance: record.performance,
        quality: record.quality,
        production: record.production,
        reject_production: record.reject_production,
        unit_packing: record.unit_packing,
        unit_measure: record.unit_measure,
        running_duration: record.running_duration,
        availability_downtime_duration: record.availability_downtime_duration,
        performance_downtime_duration: record.performance_downtime_duration,
        expected_run_rate: record.expected_run_rate
    };
}
/**
 * Calculate net production and net production quantity by unit
 * @param {Record<string, number>} production_quantity_by_unit - The production quantity by unit.
 * @param {Record<string, number>} reject_production_quantity_by_unit - The reject production quantity by unit.
 * @returns {Record<string, number | null>} The net production quantity by unit.
 */
export function calculateNetProduction(production_quantity_by_unit, reject_production_quantity_by_unit) {
    const net = {};
    for (const unit of Object.keys(production_quantity_by_unit)) {
        const production_quantity = production_quantity_by_unit[unit];
        const reject_production_quantity = reject_production_quantity_by_unit[unit];
        net[unit] =
            production_quantity !== null &&
                production_quantity !== undefined &&
                reject_production_quantity !== null &&
                reject_production_quantity !== undefined
                ? production_quantity - reject_production_quantity
                : null;
    }
    return net;
}
/**
 * Calculate OEE metrics for a period
 * @param {number | null} total_operating_time - The total operating time.
 * @param {number | null} total_available_time - The total available time.
 * @param {number | null} total_production - The total production.
 * @param {number | null} total_expected_production - The total expected production.
 * @param {number | null} total_net_production - The total net production.
 * @returns {OeeMetrics} The calculated OEE metrics.
 */
export function calculateOeeMetrics(total_operating_time, total_available_time, total_production, total_expected_production, total_net_production) {
    let availability = null;
    let performance = null;
    let quality = null;
    if (total_operating_time !== null && total_available_time !== null) {
        availability = 1;
        const calculated_availability = calculateAvailability(total_operating_time, total_available_time);
        if (!isNaN(calculated_availability) &&
            calculated_availability !== Infinity) {
            availability = calculated_availability;
        }
    }
    if (total_production !== null && total_expected_production !== null) {
        performance = 1;
        const calculated_performance = calculatePerformance(total_production, total_expected_production);
        if (!isNaN(calculated_performance) &&
            calculated_performance !== Infinity) {
            performance = calculated_performance;
        }
    }
    if (total_net_production !== null && total_production !== null) {
        quality = 1;
        const calculated_quality = calculateQuality(total_net_production, total_production);
        if (!isNaN(calculated_quality) && calculated_quality !== Infinity) {
            quality = calculated_quality;
        }
    }
    const oee = availability !== null && performance !== null && quality !== null
        ? availability * performance * quality
        : null;
    return { availability, performance, quality, oee };
}
